/**
 * System Option Calculations Utility
 *
 * Handles filtering and analysis of system options:
 * - Filtering by condensing furnace capability
 * - Energy savings calculations per option
 * - Maintenance savings calculations
 * - Total benefit analysis (energy + maintenance + rebates + avoided inflation)
 * - Monthly net cost calculations (payment - savings)
 *
 * @module systemOptionCalculations
 */

import {
  calculateSystemOptionEnergyCost,
  projectCurrentSystemCosts,
  calculateFutureEquipmentCost
} from './calculatorFunctions.js';

/* =========================
   System Option Filtering
   ========================= */

/**
 * Filter system options based on condensing furnace capability
 * @param {Array} systemOptions - All system options for the system type
 * @param {string} systemType - System type (furnaceAC, heatPump, etc.)
 * @param {string} canInstallCondensing - 'yes', 'no', or 'unsure'
 * @returns {Array} Filtered system options (top 3 most applicable)
 */
export function filterSystemOptionsByConstraints(systemOptions, systemType, canInstallCondensing) {
  // For non-gas systems, return all options (no filtering needed)
  const gasSystemTypes = ['furnaceAC', 'furnaceACPackage', 'furnaceOnly', 'boiler'];
  if (!gasSystemTypes.includes(systemType)) {
    return systemOptions.slice(0, 3); // Return top 3
  }

  // For gas systems, filter based on condensing capability
  let filtered = [...systemOptions];

  if (canInstallCondensing === 'yes') {
    // Show only condensing options (90%+ AFUE)
    filtered = filtered.filter(opt => opt.is_condensing === true);
  } else if (canInstallCondensing === 'no') {
    // Show only conventional options (80% AFUE)
    filtered = filtered.filter(opt => opt.is_condensing === false || opt.is_condensing === null);
  }
  // If 'unsure', show all options

  // Sort by option_number and return top 3
  filtered.sort((a, b) => a.option_number - b.option_number);
  return filtered.slice(0, 3);
}

/* =========================
   Energy Savings Calculations
   ========================= */

/**
 * Calculate energy savings for a system option vs current system
 * @param {Object} params - Calculation parameters
 * @returns {Object} Energy savings breakdown
 */
export function calculateEnergySavings(params) {
  const {
    systemOption,
    currentAnnualEnergyCost,
    systemType,
    squareFootage,
    climateData,
    envelope,
    electricRate,
    gasRate,
    yearsToProject = 10
  } = params;

  // Calculate new system's annual energy cost
  const newSystemCost = calculateSystemOptionEnergyCost({
    seer2: systemOption.seer2_rating,
    afue: systemOption.afue_rating,
    systemType,
    squareFootage,
    climateData,
    envelope,
    electricRate,
    gasRate
  });

  // Calculate annual savings
  const annualSavings = currentAnnualEnergyCost - newSystemCost;

  // Project savings over time with utility escalation (3% annually)
  const utilityEscalation = 0.03;
  let cumulativeSavings = 0;
  const yearlyBreakdown = [];

  for (let year = 1; year <= yearsToProject; year++) {
    // Current system cost increases with escalation
    const currentCostThisYear = currentAnnualEnergyCost * Math.pow(1 + utilityEscalation, year);

    // New system cost also increases, but starts lower
    const newCostThisYear = newSystemCost * Math.pow(1 + utilityEscalation, year);

    const savingsThisYear = currentCostThisYear - newCostThisYear;
    cumulativeSavings += savingsThisYear;

    yearlyBreakdown.push({
      year,
      currentCost: Math.round(currentCostThisYear),
      newCost: Math.round(newCostThisYear),
      savings: Math.round(savingsThisYear),
      cumulativeSavings: Math.round(cumulativeSavings)
    });
  }

  return {
    newSystemAnnualCost: Math.round(newSystemCost),
    currentSystemAnnualCost: Math.round(currentAnnualEnergyCost),
    annualSavings: Math.round(annualSavings),
    percentSavings: Math.round((annualSavings / currentAnnualEnergyCost) * 100),
    tenYearSavings: Math.round(cumulativeSavings),
    yearlyBreakdown
  };
}

/* =========================
   Maintenance Savings Calculations
   ========================= */

/**
 * Calculate maintenance savings for new system vs current system
 * @param {Object} params - Calculation parameters
 * @returns {Object} Maintenance savings breakdown
 */
export function calculateMaintenanceSavings(params) {
  const {
    currentAge,
    systemType,
    yearsToProject = 10
  } = params;

  // Current system maintenance costs (from projectCurrentSystemCosts)
  const currentSystemProjection = projectCurrentSystemCosts({
    currentAge,
    annualEnergyCost: 0, // We only care about maintenance/repairs
    squareFootage: 2000, // Not used for maintenance calc
    systemType,
    repairCost: 0,
    yearsToProject
  });

  // New system maintenance costs (minimal for first 5-10 years)
  let newSystemMaintenance = 0;
  let newSystemRepairs = 0;

  for (let year = 1; year <= yearsToProject; year++) {
    // New system maintenance
    if (year <= 5) {
      newSystemMaintenance += 125; // Basic maintenance, system under warranty
    } else if (year <= 10) {
      newSystemMaintenance += 150; // Basic maintenance, out of warranty
    }

    // New system repairs (very low probability in first 10 years)
    if (year >= 8) {
      newSystemRepairs += 70; // 5% probability × $1400 avg repair
    }
  }

  const currentSystemTotal = currentSystemProjection.totals.maintenance + currentSystemProjection.totals.repairs;
  const newSystemTotal = newSystemMaintenance + newSystemRepairs;
  const savings = currentSystemTotal - newSystemTotal;

  return {
    currentSystemMaintenance: currentSystemProjection.totals.maintenance,
    currentSystemRepairs: currentSystemProjection.totals.repairs,
    currentSystemTotal,
    newSystemMaintenance,
    newSystemRepairs,
    newSystemTotal,
    tenYearSavings: Math.round(savings)
  };
}

/* =========================
   Option Savings Analysis
   ========================= */

/**
 * Calculate comprehensive savings for a system option
 * Includes energy, maintenance, and avoided future costs
 * @param {Object} params - Calculation parameters
 * @returns {Object} Complete savings analysis
 */
export function calculateOptionSavings(params) {
  const {
    systemOption,
    currentAnnualEnergyCost,
    currentAge,
    systemType,
    squareFootage,
    climateData,
    envelope,
    electricRate,
    gasRate,
    yearsToProject = 10
  } = params;

  // Calculate energy savings
  const energySavings = calculateEnergySavings({
    systemOption,
    currentAnnualEnergyCost,
    systemType,
    squareFootage,
    climateData,
    envelope,
    electricRate,
    gasRate,
    yearsToProject
  });

  // Calculate maintenance savings
  const maintenanceSavings = calculateMaintenanceSavings({
    currentAge,
    systemType,
    yearsToProject
  });

  // Calculate avoided future equipment inflation
  // If customer waits 3 years, how much more will system cost?
  const futureInflation3yr = calculateFutureEquipmentCost(systemOption.starting_price, 3);
  const futureInflation5yr = calculateFutureEquipmentCost(systemOption.starting_price, 5);

  // Total 10-year benefit
  const totalTenYearBenefit = energySavings.tenYearSavings + maintenanceSavings.tenYearSavings;

  return {
    energy: energySavings,
    maintenance: maintenanceSavings,
    futureInflation: {
      threeYear: futureInflation3yr,
      fiveYear: futureInflation5yr
    },
    totalTenYearBenefit
  };
}

/* =========================
   Monthly Payment Calculations
   ========================= */

/**
 * Calculate monthly net cost (payment minus monthly savings)
 * @param {Object} params - Calculation parameters
 * @returns {Object} Monthly cost breakdown
 */
export function calculateMonthlyNetCost(params) {
  const {
    systemOption,
    monthlySavings  // From energy + maintenance savings / 12
  } = params;

  const monthlyPayment = systemOption.monthly_payment || 0;
  const monthlyNetCost = monthlyPayment - monthlySavings;

  return {
    monthlyPayment: Math.round(monthlyPayment),
    monthlySavings: Math.round(monthlySavings),
    monthlyNetCost: Math.round(monthlyNetCost),
    isPositiveNetCost: monthlyNetCost > 0,
    paybackMonths: monthlySavings > 0 ? Math.ceil(systemOption.starting_price / (monthlySavings * 12)) : null
  };
}

/* =========================
   Total Benefit Calculations
   ========================= */

/**
 * Calculate total comprehensive benefit of replacing with this option
 * Includes all savings, rebates, and avoided costs
 * @param {Object} params - Calculation parameters
 * @returns {Object} Total benefit analysis
 */
export function calculateTotalBenefit(params) {
  const {
    systemOption,
    savings,              // From calculateOptionSavings
    rebateGroup,          // Stackable rebate group for this option
    yearsToProject = 10
  } = params;

  // Start with energy and maintenance savings
  let totalBenefit = savings.totalTenYearBenefit;

  // Add rebate savings (immediate reduction in out-of-pocket cost)
  const totalRebates = rebateGroup ? rebateGroup.totalAmount : 0;
  totalBenefit += totalRebates;

  // Calculate net system cost after rebates
  const netSystemCost = systemOption.starting_price - totalRebates;

  // Calculate simple payback period (years)
  const annualSavings = savings.energy.annualSavings + (savings.maintenance.tenYearSavings / yearsToProject);
  const simplePayback = annualSavings > 0 ? netSystemCost / annualSavings : null;

  // Calculate ROI percentage
  const roi = netSystemCost > 0 ? (totalBenefit / netSystemCost) * 100 : null;

  return {
    totalTenYearBenefit: Math.round(totalBenefit),
    breakdown: {
      energySavings: savings.energy.tenYearSavings,
      maintenanceSavings: savings.maintenance.tenYearSavings,
      rebates: totalRebates,
      subtotal: savings.totalTenYearBenefit + totalRebates
    },
    systemCost: {
      beforeRebates: systemOption.starting_price,
      rebates: totalRebates,
      afterRebates: netSystemCost
    },
    payback: {
      simple: simplePayback ? Math.round(simplePayback * 10) / 10 : null, // Round to 1 decimal
      roi: roi ? Math.round(roi) : null
    }
  };
}

/* =========================
   System Option Comparison
   ========================= */

/**
 * Analyze all system options and return sorted by value
 * @param {Object} params - Analysis parameters
 * @returns {Array} System options with complete analysis, sorted by total benefit
 */
export function analyzeSystemOptions(params) {
  const {
    systemOptions,
    currentAnnualEnergyCost,
    currentAge,
    systemType,
    squareFootage,
    climateData,
    envelope,
    electricRate,
    gasRate,
    allRebates,
    contractorJurisdictions,
    assessmentData,
    yearsToProject = 10
  } = params;

  // Import rebate matching here to avoid circular dependencies
  // This would normally be imported at the top, but showing inline for clarity
  // In real implementation: import { matchRebatesForOption } from './rebateMatching.js';

  const analyzedOptions = systemOptions.map(option => {
    // Calculate savings for this option
    const savings = calculateOptionSavings({
      systemOption: option,
      currentAnnualEnergyCost,
      currentAge,
      systemType,
      squareFootage,
      climateData,
      envelope,
      electricRate,
      gasRate,
      yearsToProject
    });

    // Match rebates for this option (would use actual function in production)
    // const rebateGroups = matchRebatesForOption({ ... });
    // For now, assume first/best group
    const rebateGroup = null; // Placeholder - would come from rebate matching

    // Calculate total benefit
    const totalBenefit = calculateTotalBenefit({
      systemOption: option,
      savings,
      rebateGroup,
      yearsToProject
    });

    // Calculate monthly net cost
    const annualSavings = savings.energy.annualSavings + (savings.maintenance.tenYearSavings / yearsToProject);
    const monthlySavings = annualSavings / 12;

    const monthlyAnalysis = calculateMonthlyNetCost({
      systemOption: option,
      monthlySavings
    });

    return {
      ...option,
      analysis: {
        savings,
        totalBenefit,
        monthlyAnalysis,
        rebates: rebateGroup
      }
    };
  });

  // Sort by total 10-year benefit (highest first)
  analyzedOptions.sort((a, b) =>
    b.analysis.totalBenefit.totalTenYearBenefit - a.analysis.totalBenefit.totalTenYearBenefit
  );

  return analyzedOptions;
}

/* =========================
   Utility Functions
   ========================= */

/**
 * Format currency with optional sign
 * @param {number} amount - Amount to format
 * @param {boolean} showSign - Show +/- sign
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, showSign = false) {
  const formatted = '$' + Math.abs(amount).toLocaleString();
  if (showSign && amount !== 0) {
    return amount > 0 ? '+' + formatted : '-' + formatted;
  }
  return formatted;
}

/**
 * Get recommendation level based on savings
 * @param {Object} savings - Savings object from calculateOptionSavings
 * @param {Object} totalBenefit - Total benefit object
 * @returns {Object} Recommendation with level and message
 */
export function getRecommendationLevel(savings, totalBenefit) {
  const roi = totalBenefit.payback.roi;

  if (roi >= 100) {
    return {
      level: 'highly_recommended',
      color: '#10b981',
      icon: '⭐',
      message: 'Excellent Investment',
      description: 'This option will pay for itself and deliver substantial long-term savings.'
    };
  } else if (roi >= 50) {
    return {
      level: 'recommended',
      color: '#3b82f6',
      icon: '✓',
      message: 'Strong Value',
      description: 'This option offers solid returns and meaningful savings over time.'
    };
  } else if (roi >= 25) {
    return {
      level: 'good_value',
      color: '#f59e0b',
      icon: '◐',
      message: 'Good Value',
      description: 'This option will save money, though payback period is longer.'
    };
  } else {
    return {
      level: 'consider',
      color: '#64748b',
      icon: '○',
      message: 'Consider Carefully',
      description: 'This option has longer payback. Weigh comfort benefits vs. financial savings.'
    };
  }
}
